import {
    useReducer,
    useEffect,
    useCallback,
    useContext,
    useMemo
} from 'react';

import { UserContext } from '../context/UserContext';
import Swal from 'sweetalert2';

import {
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

import SearchNotFound from '../utils/SearchNotFound';

import CustomSearch from '../components/custom_components/CustomSearch';
import CustomButton from '../components/custom_components/CustomButton';
import CustomRefreshButton from '../components/custom_components/CustomRefreshButton';
import CustomIconButton from '../components/custom_components/CustomIconButton';

import RegisterCustomer from '../components/manage_customer/RegisterCustomer';
import EditCustomer from '../components/manage_customer/EditCustomer';

const apiUrl = process.env.REACT_APP_API_URL;

// REDUCER
const initialState = {
    customerList: [],

    search: '',
    debouncedSearch: '',

    loadingCustomers: false,

    customerRegOpen: false,
    customerEditOpen: false,

    customerData: {}
};

function customerReducer(state, action) {

    switch (action.type) {

        case 'SET_CUSTOMERS':
            return {
                ...state,
                customerList: action.payload
            };

        case 'ADD_CUSTOMER':
            return {
                ...state,
                customerList: [
                    action.payload,
                    ...state.customerList
                ]
            };

        case 'UPDATE_CUSTOMER':
            return {
                ...state,
                customerList: state.customerList.map(customer =>
                    customer.CustomerID === action.payload.CustomerID
                        ? {
                            ...customer,
                            CustomerName: action.payload.CustomerName,
                            SecondName: action.payload.SecondName,
                            FbLink: action.payload.FbLink
                        }
                        : customer
                )
            };

        case 'SET_SEARCH':
            return {
                ...state,
                search: action.payload
            };

        case 'SET_DEBOUNCED_SEARCH':
            return {
                ...state,
                debouncedSearch: action.payload
            };

        case 'SET_LOADING':
            return {
                ...state,
                loadingCustomers: action.payload
            };

        case 'OPEN_REGISTER':
            return {
                ...state,
                customerRegOpen: true
            };

        case 'CLOSE_REGISTER':
            return {
                ...state,
                customerRegOpen: false
            };

        case 'OPEN_EDIT':
            return {
                ...state,
                customerEditOpen: true,
                customerData: action.payload
            };


        case 'CLOSE_EDIT':
            return {
                ...state,
                customerEditOpen: false,
                customerData: {}
            };


        default:
            return state;
    }
}

// FACEBOOK LINK CELL
const FbLinkCell = ({ data }) => {
    const link = data.FbLink || '';
    const isValid = /^https?:\/\//i.test(link);
    return (
        <TableCell>
            {isValid ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: 'gray',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                    }}
                >
                    <LinkIcon fontSize="small" />
                    {link}
                </a>
            ) : (
                <span
                    style={{
                        color: 'lightgray',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'not-allowed'
                    }}
                >
                    <LinkIcon fontSize="small" />
                    {link || 'No Link'}
                </span>
            )}
        </TableCell>
    );
};

// MAIN COMPONENT
export default function ManageCustomer() {

    const { session } = useContext(UserContext);

    const [state, dispatch] = useReducer(
        customerReducer,
        initialState
    );

    const {
        customerList,
        search,
        debouncedSearch,
        loadingCustomers,
        customerRegOpen,
        customerEditOpen,
        customerData
    } = state;

    const tableHeaders = useMemo(() => [
        'Customer ID',
        'Customer Name',
        'Secondary Name',
        'Link',
        'Actions'
    ], []);

    // Determine whether the current user can delete.
    const canDelete = useMemo(() => {
        return ['CEO', 'Manager'].includes(session?.role);
    }, [session?.role]);

    // Memoize customer list.
    const customers = useMemo(() => {
        return customerList.map(customer => ({
            ...customer,
            hasSecondName: Boolean(customer.SecondName),
            hasFbLink: Boolean(
                customer.FbLink &&
                /^https?:\/\//i.test(customer.FbLink)
            )
        }));
    }, [customerList]);

    // DISPLAY CUSTOMERS
    const handleDisplayCustomers = useCallback(async () => {

        dispatch({
            type: 'SET_LOADING',
            payload: true
        });

        try {

            const response = await fetch(
                `${apiUrl}manage_customer/get_customers.php`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        search: debouncedSearch
                    })
                }
            );

            const result = await response.json();

            if (result.status === 'success') {

                dispatch({
                    type: 'SET_CUSTOMERS',
                    payload: result.data || []
                });

            } else if (result.status === 'error') {

                Swal.fire({
                    text: result.message,
                    icon: 'info'
                });

            }

        } catch (error) {

            Swal.fire({
                title: 'Server Error!',
                text: error.message || String(error),
                icon: 'error'
            });

            console.error('Server error:', error);

        } finally {
            dispatch({
                type: 'SET_LOADING',
                payload: false
            });
        }

    }, [debouncedSearch]);

    // INITIAL LOAD + SEARCH
    useEffect(() => {
        handleDisplayCustomers();
    }, [handleDisplayCustomers]);

    // DEBOUNCE SEARCH
    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch({
                type: 'SET_DEBOUNCED_SEARCH',
                payload: search
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // SEARCH HANDLER
    const handleSearchChange = useCallback((value) => {
        dispatch({
            type: 'SET_SEARCH',
            payload: value
        });
    }, []);

    // OPEN EDIT
    const handleEditCustomer = useCallback((info) => {

        dispatch({
            type: 'OPEN_EDIT',
            payload: info
        });

    }, []);

    // DELETE
    const handleDelete = useCallback((customerID) => {
        Swal.fire({
            title: 'Confirm delete!',
            text: 'As you delete this customer, it will delete all its orders.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true,
            allowOutsideClick: false,
            allowEscapeKey: false
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Confirm delete!',
                    text: 'This feature is currently in development!',
                    icon: 'info'
                });
            }
        });
    }, []);

    // REGISTER CALLBACK
    const handleCustomerRegistered = useCallback((val) => {
        dispatch({
            type: 'ADD_CUSTOMER',
            payload: val
        });
    }, []);

    // EDIT CALLBACK
    const handleCustomerUpdated = useCallback((val) => {
        dispatch({
            type: 'UPDATE_CUSTOMER',
            payload: val
        });
    }, []);

    // MODAL CLOSE HANDLERS
    const handleCloseRegister = useCallback(() => {
        dispatch({
            type: 'CLOSE_REGISTER'
        });
    }, []);

    const handleCloseEdit = useCallback(() => {
        dispatch({ type: 'CLOSE_EDIT' });
    }, []);

    const handleOpenRegister = useCallback(() => {
        dispatch({ type: 'OPEN_REGISTER' });
    }, []);

    return (
        <Stack>

            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                    width: '100%'
                }}
            >

                <Stack direction="row" gap={2}  >
                    <CustomSearch
                        placeholder="Search by Customer Name"
                        md='36ch'
                        value={search}
                        onChange={handleSearchChange}
                    />
                    <CustomRefreshButton
                        label="Refresh"
                        loading={loadingCustomers}
                        onClick={handleDisplayCustomers}
                    />
                </Stack>

                <CustomButton
                    label="New"
                    startIcon={<PersonAddAlt1Icon />}
                    onClick={handleOpenRegister}
                />

            </Stack>

            <TableContainer
                sx={{
                    mt: 2,
                    borderRadius: 1.2,
                    border: '1px solid #e5e5e5',
                    backgroundColor: '#fff',
                    maxHeight: '100vh',
                    overflowY: 'auto'
                }}
            >

                {/* TABLE */}
                <Table size="small" stickyHeader >

                    {/* HEADER */}
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#111', height: 44 }} >
                            {tableHeaders.map((header) => (
                                <TableCell
                                    key={header}
                                    align={
                                        header === 'Actions'
                                            ? 'center'
                                            : 'left'
                                    }
                                    sx={{
                                        bgcolor: '#1C2536',
                                        color: '#94A3B8',
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: '0.6px',
                                        textTransform: 'uppercase',
                                        borderBottom: 'none',
                                        py: 1,
                                        width:
                                            header === 'Actions'
                                                ? 110
                                                : 'auto'
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>

                        {/* LOADING */}
                        {loadingCustomers ? (

                            Array.from({ length: 3 }).map((_, index) => (
                                <TableRow key={`skeleton-${index}`}>

                                    <TableCell>
                                        <Skeleton variant="text" />
                                    </TableCell>

                                    <TableCell>
                                        <Skeleton variant="text" />
                                    </TableCell>

                                    <TableCell>
                                        <Skeleton variant="text" />
                                    </TableCell>

                                    <TableCell>
                                        <Skeleton variant="text" />
                                    </TableCell>

                                    <TableCell align="right">

                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            justifyContent="flex-end"
                                        >

                                            <Skeleton
                                                variant="rounded"
                                                width={34}
                                                height={34}
                                                sx={{
                                                    borderRadius: '7px'
                                                }}
                                            />

                                            <Skeleton
                                                variant="rounded"
                                                width={34}
                                                height={34}
                                                sx={{
                                                    borderRadius: '7px'
                                                }}
                                            />

                                        </Stack>

                                    </TableCell>

                                </TableRow>
                            ))

                        ) : customers.length === 0 ? (

                            /* EMPTY */
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    align="center"
                                    sx={{
                                        borderBottom: 'none'
                                    }}
                                >
                                    <SearchNotFound />
                                </TableCell>
                            </TableRow>

                        ) : (

                            /* CUSTOMER ROWS */
                            customers.map((data) => (

                                <TableRow
                                    key={data.CustomerID}
                                    sx={{
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            backgroundColor: '#dcfbff'
                                        },
                                        '& td': {
                                            borderBottom:
                                                '1px solid #f3f3f3',
                                            py: 1,
                                            fontSize: 13
                                        }
                                    }}
                                >

                                    {/* CUSTOMER ID */}
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: '#444'
                                        }}
                                    >
                                        {data.CustomerID}
                                    </TableCell>

                                    {/* CUSTOMER NAME */}
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: '#222'
                                        }}
                                    >
                                        {data.CustomerName}
                                    </TableCell>

                                    {/* SECOND NAME */}
                                    <TableCell>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color:
                                                    data.hasSecondName
                                                        ? '#000000'
                                                        : '#94A3B8'
                                            }}
                                        >
                                            {data.hasSecondName
                                                ? data.SecondName
                                                : <em>None</em>}
                                        </Typography>
                                    </TableCell>

                                    {/* FACEBOOK LINK */}
                                    <FbLinkCell data={data} />

                                    {/* ACTIONS */}
                                    <TableCell align="center">
                                        <Stack
                                            direction="row"
                                            justifyContent="center"
                                            alignItems="center"
                                            spacing={1}
                                        >

                                            {/* EDIT */}
                                            <CustomIconButton
                                                type="success"
                                                icon={
                                                    <EditIcon fontSize="small" />
                                                }
                                                onClick={() =>
                                                    handleEditCustomer(data)
                                                }
                                            />

                                            {/* DELETE */}
                                            {canDelete && (

                                                <CustomIconButton
                                                    type="delete"
                                                    icon={
                                                        <DeleteIcon fontSize="small" />
                                                    }
                                                    onClick={() =>
                                                        handleDelete(
                                                            data.CustomerID
                                                        )
                                                    }
                                                />

                                            )}

                                        </Stack>

                                    </TableCell>

                                </TableRow>

                            ))

                        )}

                    </TableBody>

                </Table>

            </TableContainer>

            {/* REGISTER CUSTOMER */}
            <RegisterCustomer
                open={customerRegOpen}
                onClose={handleCloseRegister}
                onUpdate={handleCustomerRegistered}
            />

            {/* EDIT CUSTOMER */}
            <EditCustomer
                open={customerEditOpen}
                onClose={handleCloseEdit}
                info={customerData}
                onUpdate={handleCustomerUpdated}
            />

        </Stack>
    );
}

